// store/modules/chat.js
import { sharedText } from '../core/utils'
import { sanitizeChatHtml } from '../core/html-sanitizer'
import httpService from '../core/http-service'
import gtm from '../core/enhanced-ecommerce-gtm'
import Vue from 'vue'

// Create a Vue instance to use as an event bus
const chatEventBus = new Vue()

const getInitialMessage = () => {
  try {
    return sanitizeChatHtml(sharedText('caiInitialChatMessage'))
  } catch (error) {
    return "Hi, I'm CAI! Olsson Parts assistant bot. How can I help you?"
  }
}

// Initial state
const state = {
  messages: [
    {
      exchangeId: null,
      sender: 'bot',
      text: getInitialMessage(),
      timestamp: Date.now()
    }
  ],
  username: null,
  // A signed-in user can have no username (see setOwner), so this is what tells their
  // chat apart from an anonymous one
  ownerAuthenticated: false,
  sessionId: null
}

const getters = {
  messages: (state) => state.messages,
  username: (state) => state.username,
  sessionId: (state) => state.sessionId,
  lastMessage: (state) => state.messages[state.messages.length - 1],
  messagesByExchangeId: (state) => (exchangeId) => {
    return state.messages.filter(msg => msg.exchangeId === exchangeId)
  }
}

const mutations = {
  ADD_MESSAGE(state, message) {
    state.messages.push({
      ...message,
      // Bot text is rendered as HTML, so it is sanitized before it reaches the
      // store (and therefore before it is persisted to local storage).
      // User text is rendered as plain text and is stored verbatim.
      text: message.sender === 'bot' ? sanitizeChatHtml(message.text) : message.text,
      timestamp: message.timestamp || Date.now()
    })
  },

  SET_USERNAME(state, username) {
    state.username = username
  },

  SET_OWNER_AUTHENTICATED(state, ownerAuthenticated) {
    state.ownerAuthenticated = ownerAuthenticated
  },

  SET_SESSION_ID(state, sessionId) {
    state.sessionId = sessionId
  },

  CLEAR_MESSAGES(state) {
    state.messages = []
  },

  RESET_CHAT(state) {
    state.messages = [
      {
        exchangeId: null,
        sender: 'bot',
        text: getInitialMessage(),
        timestamp: Date.now()
      }
    ]
    state.username = null
    state.ownerAuthenticated = false
    state.sessionId = null
  },

  UPDATE_LAST_MESSAGE(state, updates) {
    if (state.messages.length > 0) {
      const lastIndex = state.messages.length - 1
      const previous = state.messages[lastIndex]
      const updated = {
        ...previous,
        ...updates
      }
      if (updated.sender === 'bot' && 'text' in updates) {
        updated.text = sanitizeChatHtml(updates.text)
      }
      state.messages[lastIndex] = updated
    }
  },

  REMOVE_MESSAGE(state, index) {
    state.messages.splice(index, 1)
  }
}

const actions = {
  addMessage({ commit }, message) {
    const messageWithDefaults = {
      exchangeId: message.exchangeId || null,
      sender: message.sender,
      text: message.text,
      options: message.options || []
    }
    commit('ADD_MESSAGE', messageWithDefaults)
  },

  addUserMessage({ commit }, { text, exchangeId }) {
    commit('ADD_MESSAGE', {
      exchangeId,
      sender: 'user',
      text
    })
  },

  addBotMessage({ commit }, { text, exchangeId, options = [], isTyping = false }) {
    commit('ADD_MESSAGE', {
      exchangeId,
      sender: 'bot',
      text,
      options,
      isTyping
    })
  },

  setUsername({ commit }, username) {
    commit('SET_USERNAME', username)
  },

  // userName is null for a signed-in contact that is not connected to a customer yet,
  // because the server sends no user model for them
  setOwner({ commit }, { isAuthenticated, userName }) {
    commit('SET_USERNAME', userName || null)
    commit('SET_OWNER_AUTHENTICATED', !!isAuthenticated)
  },

  setSessionId({ commit }, sessionId) {
    commit('SET_SESSION_ID', sessionId)
  },

  minimizeChatbot() {
    chatEventBus.$emit('minimize-chatbot')
  },

  clearMessages({ commit }) {
    commit('CLEAR_MESSAGES')
  },

  resetChat({ commit }) {
    commit('RESET_CHAT')
  },

  // Every chat teardown funnels through here, so this is the single place the backend
  // gets told the conversation ended. Lives in the store rather than the chat-bot
  // component because logout must work on pages where the chat-bot is not mounted.
  async endSession({ state, commit }, { notifyServer = true } = {}) {
    const sessionId = state.sessionId || localStorage.getItem('sessionId')

    chatEventBus.$emit('session-ended')
    localStorage.removeItem('sessionId')
    localStorage.removeItem('chatLastActivity')
    commit('RESET_CHAT')

    if (!notifyServer || !sessionId) {
      return
    }

    try {
      await httpService.post(`cai/chat/${sessionId}/end`)
    } catch (error) {
      // Best-effort cleanup — must never block the user from closing the chat or logging out
      console.log('Failed to end chat conversation: ' + error)
    }
  },

  trackSessionEnd({ state }, reason) {
    const sessionId = state.sessionId || localStorage.getItem('sessionId')
    if (!sessionId) {
      return
    }

    try {
      const chatConversion = JSON.parse(localStorage.getItem('chat_conversion') || 'null')
      if (!chatConversion || chatConversion.chatSessionId !== sessionId) {
        gtm.chatNoConversion(sessionId)
      }
      gtm.chatSessionEnd(sessionId, state.messages.length, reason)
    } catch (error) {
      // Analytics must never block the user from closing the chat or logging out
      console.log('Failed to track chat session end: ' + error)
    }
    localStorage.removeItem('chat_conversion')
  },

  async endSessionForLogout({ dispatch }) {
    await dispatch('trackSessionEnd', 'logout')
    await dispatch('endSession')
  },

  // Catches identity changes that bypass our logout buttons (cookie expiry, forced
  // sign-out, logout in another tab, impersonation), so a chat never outlives its owner.
  ensureSessionOwner({ state, commit, dispatch }, { isAuthenticated, userName }) {
    const sessionId = state.sessionId || localStorage.getItem('sessionId')
    if (!sessionId) {
      return
    }

    if (!state.username && !state.ownerAuthenticated) {
      // An anonymous chat carries over into the login that follows it
      if (isAuthenticated) {
        return dispatch('setOwner', { isAuthenticated, userName })
      }
      return
    }

    // Names can only be compared when both are known: a signed-in contact without a
    // customer has no user model, and so no username
    const isOwnerGone = !isAuthenticated ||
      (!!userName && !!state.username && userName.toLowerCase() !== state.username.toLowerCase())

    if (isOwnerGone) {
      dispatch('trackSessionEnd', 'owner_changed')
      return dispatch('endSession')
    }
  },

  updateLastMessage({ commit }, updates) {
    commit('UPDATE_LAST_MESSAGE', updates)
  },

  removeMessage({ commit }, index) {
    commit('REMOVE_MESSAGE', index)
  }
}

// Another tab ended this session (logout or clear). Drop it here too, otherwise the next
// persisted mutation in this tab writes the old chat back to local storage. A store plugin
// rather than part of the chat-bot, because that is not mounted on every page. Not tracked,
// because the tab that ended the session already reported it.
export function endSessionEndedInOtherTab(store) {
  window.addEventListener('storage', (event) => {
    if (event.storageArea !== window.localStorage || event.key !== 'sessionId') return
    if (event.newValue === null && event.oldValue && event.oldValue === store.state.chat.sessionId) {
      store.dispatch('chat/endSession', { notifyServer: false })
    }
  })
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

// Export the event bus for components to use
export { chatEventBus }
